import { create } from 'zustand';
import { api } from '../services/api';
import { getSocket } from '../services/socket';

interface ChatUser {
  id: string;
  name: string;
  profileImageUrl: string | null;
  role?: string;
}

export interface ChatRoom {
  roomId: string;
  type: 'USER_AGENT' | 'AGENT_AGENT';
  lastMessage: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  otherUser: ChatUser | null;
}

export interface ChatMessage {
  id: string;
  roomId: string;
  senderId: string;
  content: string;
  readAt: string | null;
  createdAt: string;
  sender: ChatUser;
}

export interface ContactCustomer {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  profileImageUrl: string | null;
}

export interface ContactAgent {
  id: string;
  agentId: string;
  userId: string | null;
  name: string;
  officeName: string;
  profileImageUrl: string | null;
  phone: string;
  dongtanArea: string | null;
}

export interface ChatContacts {
  customers: ContactCustomer[];
  agents: ContactAgent[];
}

interface ChatState {
  rooms: ChatRoom[];
  currentRoomId: string | null;
  messages: ChatMessage[];
  totalUnread: number;
  typingUsers: Record<string, boolean>;
  isLoading: boolean;
  contacts: ChatContacts | null;

  fetchRooms: () => Promise<void>;
  fetchMessages: (roomId: string, page?: number) => Promise<{ total: number }>;
  setCurrentRoom: (roomId: string | null) => void;
  sendMessage: (roomId: string, content: string) => void;
  addMessage: (message: ChatMessage) => void;
  setTyping: (roomId: string, userId: string, isTyping: boolean) => void;
  fetchTotalUnread: () => Promise<void>;
  createRoom: (targetUserId: string, type?: string) => Promise<string>;
  fetchContacts: () => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  rooms: [],
  currentRoomId: null,
  messages: [],
  totalUnread: 0,
  typingUsers: {},
  isLoading: false,
  contacts: null,

  fetchRooms: async () => {
    try {
      const { data } = await api.get('/chat/rooms');
      set({ rooms: data.data || [] });
    } catch { /* silent */ }
  },

  fetchMessages: async (roomId, page = 1) => {
    set({ isLoading: true });
    try {
      const { data } = await api.get(`/chat/rooms/${roomId}/messages`, {
        params: { page, limit: 50 },
      });
      set({ messages: data.data || [], currentRoomId: roomId });
      return { total: data.meta?.total || 0 };
    } catch {
      return { total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  setCurrentRoom: (roomId) => set({ currentRoomId: roomId }),

  sendMessage: (roomId, content) => {
    const socket = getSocket();
    socket?.emit('chat:message', { roomId, content });
  },

  addMessage: (message) => {
    set((state) => {
      if (state.currentRoomId === message.roomId) {
        // 중복 방지
        const exists = state.messages.some((m) => m.id === message.id);
        if (exists) return state;
        return { messages: [...state.messages, message] };
      }
      return state;
    });
  },

  setTyping: (roomId, userId, isTyping) => {
    set((state) => ({
      typingUsers: { ...state.typingUsers, [`${roomId}:${userId}`]: isTyping },
    }));
  },

  fetchTotalUnread: async () => {
    try {
      const { data } = await api.get('/chat/unread');
      set({ totalUnread: data.data?.count || 0 });
    } catch { /* silent */ }
  },

  createRoom: async (targetUserId, type = 'USER_AGENT') => {
    const { data } = await api.post('/chat/rooms', { targetUserId, type });
    return data.data.id;
  },

  fetchContacts: async () => {
    try {
      const { data } = await api.get('/chat/contacts');
      set({ contacts: data.data || null });
    } catch { /* silent */ }
  },
}));
